import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ArrowLink } from 'components/link/ArrowLink';
import { NoValue } from 'components/no_value/NoValue';
import { ReactNode } from 'react';
import { InfoPopup } from 'shared/info_popup/InfoPopup';
import styled from 'styled-components';
import {
    borderColor,
    navBorderColor,
    primaryText,
    shadeBackgroundColor,
} from 'styles/theme';

const CountWrapper = styled.div`
    display: grid;
    grid-template-areas:
        'icon value'
        'icon title';

    grid-template-columns: 32px 1fr;

    .icon {
        grid-area: icon;
        align-content: center;
        justify-content: center;
    }
    .title {
        grid-area: title;
        font-weight: 400;
        font-size: 14px;
    }
    .value {
        grid-area: value;
    }

    background-color: ${shadeBackgroundColor};
    padding-inline: 1.2rem;
    padding-block: 1.2rem;
    border: 1px solid ${borderColor};
    border-radius: 8px;
    border-bottom: 2px solid ${navBorderColor};

    position: relative;

    .info_icon {
        position: absolute;
        top: 50%;
        right: 0;
        transform: translateX(-50%) translateY(-50%);
    }
`;

const CountIcon = styled.span`
    font-size: 2rem;
    color: ${primaryText};
    & > svg {
        width: 2rem;
        height: 2rem;
    }
    & * {
        fill: ${primaryText};
    }
`;

const CountTitle = styled.span`
    font-weight: 500;
    font-size: 1.5rem;
`;

const CountValue = styled.span`
    font-weight: 500;
    font-size: 1.7rem;
`;

function extractIcon(icon: IconDefinition | JSX.Element | undefined) {
    if (!icon) {
        return undefined;
    }
    if ('icon' in icon) {
        return <FontAwesomeIcon icon={icon} fontSize={'2rem'} />;
    }
    return icon;
}

export function Count({
    title,
    value,
    prefix,
    suffix,
    icon,
    link,
    info,
}: {
    title: string;
    /**
     * undefined === loading
     * null === no value
     */
    value: string | number | null | undefined;
    prefix?: string;
    suffix?: string;
    icon?: IconDefinition | JSX.Element;
    link?: string;
    /**
     * informational text to be rendered in an info popup
     */
    info?: string | ReactNode;
}) {
    return (
        <CountWrapper>
            <CountIcon className="icon">{extractIcon(icon)}</CountIcon>
            <CountTitle className="title">
                {link ? <ArrowLink to={link}>{title}</ArrowLink> : title}
            </CountTitle>
            {value !== undefined ? (
                <CountValue className="value">
                    {value === null ? (
                        <NoValue />
                    ) : (
                        <>
                            {prefix}
                            {typeof value === 'number' ? value.toLocaleString() : value}
                            {suffix}
                        </>
                    )}
                </CountValue>
            ) : (
                <span>Loading...</span>
            )}
            {info && (
                <InfoPopup
                    title={'show more information'}
                    type="info"
                    triggerClassName="info_icon"
                >
                    {info}
                </InfoPopup>
            )}
        </CountWrapper>
    );
}
